import { config } from './config';
import { CodeRefused, storeSession, type Session } from './auth';
import type { Money } from './types';
import { INVITATION } from './joining';

// N40 — the invitation's calls on the desk, pre-session by definition: the
// link's read, the yes that asks for one code, the code that carries the yes out
// and signs them in here, Not now, and This is not me. The rules are the API's
// (api/src/handlers/invitations.ts); the phone has the same calls in
// app/src/invitation.ts. No Authorization header: the signed link is the
// capability.

export interface InvitationView {
  status: 'invited' | 'declined' | 'joined';
  businessName: string;
  hotelName: string;
  invitedBy?: string;
  phoneHint?: string;
  // Absent until George's terms are published (N40 fix round).
  termsVersion?: string;
  history?: { bookingsSettled: number; typicalAmount: Money };
}

export type LinkRead =
  | { kind: 'ok'; invitation: InvitationView }
  | { kind: 'expired'; hotelName: string }
  | { kind: 'invalid' }
  | { kind: 'failed' };

const path = (token: string, verb = '') => `${config().apiBase}/invitations/${encodeURIComponent(token)}${verb ? `/${verb}` : ''}`;
const post = (url: string, body: unknown) =>
  fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export async function readInvitation(token: string): Promise<LinkRead> {
  if (!token) return { kind: 'invalid' };
  let res: Response;
  try {
    res = await fetch(path(token));
  } catch {
    return { kind: 'failed' };
  }
  const body = await res.json().catch(() => ({}));
  if (res.ok) return { kind: 'ok', invitation: body as InvitationView };
  if (res.status === 410) return { kind: 'expired', hotelName: String(body?.details?.hotelName ?? INVITATION.theHotel) };
  if (res.status === 404) return { kind: 'invalid' };
  return { kind: 'failed' };
}

export interface CodeOnItsWay {
  challengeId: string;
  phoneHint: string;
  resendAfterSeconds: number;
  delivery: 'sent' | 'simulated';
  message: string;
}

export class CodeNotSent extends Error {
  constructor(
    public kind: 'hotel-number' | 'number' | 'terms' | 'used' | 'wait' | 'not-delivered' | 'gone' | 'expired' | 'failed',
    message: string,
  ) {
    super(message);
    this.name = 'CodeNotSent';
  }
}

export async function askForCode(token: string, opts: { phone?: string; termsVersion?: string }): Promise<CodeOnItsWay> {
  let res: Response;
  try {
    res = await post(path(token, 'code'), { ...opts, surface: 'platform' });
  } catch {
    throw new CodeNotSent('failed', INVITATION.noConnection);
  }
  const body = await res.json().catch(() => ({}));
  if (res.status === 202) return body as CodeOnItsWay;
  const message = typeof body?.message === 'string' ? body.message : '';
  if (res.status === 422) throw new CodeNotSent(body?.details?.whose === 'hotel' ? 'hotel-number' : 'number', message);
  // N40 fix round: a used link sends no code to anyone — they sign in with their phone.
  if (res.status === 409) throw new CodeNotSent(body?.code === 'already_said_yes' ? 'used' : 'terms', message);
  if (res.status === 429) throw new CodeNotSent('wait', message);
  if (res.status === 502) throw new CodeNotSent('not-delivered', message);
  if (res.status === 410) throw new CodeNotSent('expired', message);
  if (res.status === 404) throw new CodeNotSent('gone', message);
  throw new CodeNotSent('failed', INVITATION.failed);
}

export type YesResult = { kind: 'signed-in'; session: Session } | { kind: 'sign-in-instead' };

export async function sayYes(token: string, opts: { challengeId: string; code?: string; termsVersion?: string }): Promise<YesResult> {
  let res: Response;
  try {
    res = await post(path(token, 'accept'), { ...opts, surface: 'platform' });
  } catch {
    throw new Error(INVITATION.connectionDropped);
  }
  const body = await res.json().catch(() => ({}));
  if (res.status === 200 && typeof body.idToken === 'string') return { kind: 'signed-in', session: storeSession(body.idToken) };
  if (res.status === 202) return { kind: 'sign-in-instead' };
  // A yes already carried out signs in once (N40 fix round): the ordinary sign-in.
  if (res.status === 409 && body?.code === 'already_said_yes') return { kind: 'sign-in-instead' };
  const message = typeof body?.message === 'string' ? body.message : '';
  if (body?.code === 'code_wrong' && typeof body?.details?.challengeId === 'string') {
    throw new CodeRefused('wrong', message, body.details.challengeId);
  }
  if (body?.code === 'too_many_tries') throw new CodeRefused('too_many', message);
  if (res.status === 401) throw new CodeRefused('expired', message);
  if (res.status === 409 || res.status === 410 || res.status === 404) {
    throw new CodeNotSent(res.status === 409 ? 'terms' : res.status === 410 ? 'expired' : 'gone', message);
  }
  throw new Error(INVITATION.codeDidNotGoThrough);
}

export interface FollowUp {
  at: string;
  by: 'text' | 'email';
  simulated: boolean;
}

/** *Not now*: recorded, and — N40 — the one reminder, when one is coming: when, how, and whether only to the demo inbox. */
export async function notNow(token: string): Promise<{ followUp?: FollowUp }> {
  const res = await post(path(token, 'decline'), {});
  if (!res.ok) throw new Error(INVITATION.didNotGoThrough);
  const body = await res.json().catch(() => ({}));
  if (typeof body?.followUpAt !== 'string') return {};
  return { followUp: { at: body.followUpAt, by: body.followUpBy === 'email' ? 'email' : 'text', simulated: body.followUpSimulated === true } };
}

export async function notMe(token: string): Promise<void> {
  const res = await post(path(token, 'burn'), {});
  if (!res.ok) throw new Error(INVITATION.didNotGoThrough);
}

/** "This is not me" when the page is closing during its hold (N40 review): the burn goes with the page. */
export function notMeAsThePageCloses(token: string): void {
  const url = path(token, 'burn');
  try {
    if (navigator.sendBeacon?.(url, new Blob(['{}'], { type: 'application/json' }))) return;
  } catch {
    /* fall through to a keepalive fetch */
  }
  void fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}', keepalive: true }).catch(() => undefined);
}

// A phone the sign-in proved (the number had an open invitation and no
// account), kept for this tab only, for the yes — one key on both surfaces
// (app/src/invitation.ts), so a hand-off between them keeps it (N40 review).
const PROVEN = 'iso.joinProven.';

export function rememberProven(token: string, challengeId: string): void {
  try {
    sessionStorage.setItem(PROVEN + token, challengeId);
  } catch {
    /* the yes will ask for a code instead */
  }
}

export function provenFor(token: string): string | null {
  try {
    return sessionStorage.getItem(PROVEN + token);
  } catch {
    return null;
  }
}

export function forgetProven(token: string): void {
  try {
    sessionStorage.removeItem(PROVEN + token);
  } catch {
    /* nothing to forget */
  }
}
